import {useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {getPageDescriptionI18n, getPageTitleI18n, getRoutePattern} from '../utils/pageMetadata';

/**
 * Custom hook to generate internationalized page metadata based on current route and election data
 * @param {Object} electionData - Election data object (can be null during loading)
 * @returns {Object} Object containing title and description
 */
export function usePageMetadata(electionData = null) {
    const location = useLocation();
    const { t } = useTranslation('common');

    return useMemo(() => {
        const routePattern = getRoutePattern(location.pathname);
        const title = getPageTitleI18n(routePattern, electionData, t);
        const description = getPageDescriptionI18n(routePattern, electionData, t);

        return {title, description, routePattern};
    }, [location.pathname, electionData, t]);
}

/**
 * Alternative hook for components that want to specify the route pattern directly
 * Useful for testing or when you know the exact route pattern
 * @param {string} routePattern - Explicit route pattern
 * @param {Object} electionData - Election data object
 * @returns {Object} Object containing title and description
 */
export function usePageMetadataWithRoute(routePattern, electionData = null) {
    const { t } = useTranslation('common');

    return useMemo(() => {
        const title = getPageTitleI18n(routePattern, electionData, t);
        const description = getPageDescriptionI18n(routePattern, electionData, t);

        return {title, description, routePattern};
    }, [routePattern, electionData, t]);
}